// McpToolFilterEditor — per-server CUSTOM MCP message filters (if_all + enforce
// over JSON-pointer properties). This is the self-hosted incorporation of the
// PaaS McpMessageFilterEditor: it authors filters IN ADDITION to a server's
// allow/deny lists (which the McpServerTable already edits and the UC deployer
// still auto-derives its own filters from).
//
// Slots into Policy.tsx renderSurfaceBody("mcp") beneath <McpServerTable/>.
// See SELFHOSTED_TOOL_FILTER_DESIGN.md (§3, §5) for placement + integration.
//
// STATUS: scaffold. NOT yet imported by Policy.tsx. Saving policies that carry
// `message_filter` requires the Go changes in §4 (model.go + schema.go +
// uc.go); until then the backend 400s (DisallowUnknownFields).
//
// Conventions (corenet CLAUDE.md): AntD primitives first, theme.useToken() for
// colors, mono (token.fontFamilyCode) for JSON-pointers + names, Lucide icons,
// corenet ChipEditor for the values list. No hardcoded hex, no Tailwind status.
import { useState, type ReactNode } from "react";
import { Button, Collapse, Flex, Input, Segmented, Select, Tag, Typography, theme } from "antd";
import { Plus, X } from "lucide-react";
import { ChipEditor } from "@bluerock/corenet/policy";
import {
  MCP_METHODS,
  MCP_FILTER_TEMPLATES,
  type McpMessageFilter,
  type McpIfAllCondition,
  type McpEnforceClause,
  type McpFilterTemplateId,
  type ExtendedMcpServer,
} from "@/lib/policyExt";

const { Text } = Typography;

interface Props {
  /** mcp.servers — cast to ExtendedMcpServer at the call site in Policy.tsx. */
  servers: Record<string, ExtendedMcpServer>;
  editing?: boolean;
  /** Same setter shape McpServerTable uses; patch merges onto servers[name]. */
  onServerChange: (name: string, patch: Partial<ExtendedMcpServer>) => void;
}

export default function McpToolFilterEditor({ servers, editing, onServerChange }: Props) {
  const { token } = theme.useToken();
  const entries = Object.entries(servers);

  // Read-only: only show servers that actually have custom filters.
  const visible = editing ? entries : entries.filter(([, s]) => (s.message_filter?.length ?? 0) > 0);
  if (visible.length === 0) {
    return editing ? (
      <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 12 }}>
        Add a server above, then define advanced message filters here.
      </Text>
    ) : null;
  }

  return (
    <div style={{ marginTop: 16 }}>
      <Text
        strong
        style={{ fontFamily: "var(--font-serif, 'Source Serif 4 Variable', serif)", fontSize: 13, display: "block", marginBottom: 8 }}
      >
        Advanced message filters
      </Text>
      <Collapse
        size="small"
        items={visible.map(([name, srv]) => {
          const filters = srv.message_filter ?? [];
          return {
            key: name,
            label: (
              <Flex align="center" gap={8}>
                <code style={{ fontFamily: token.fontFamilyCode }}>{name}</code>
                <Tag>{filters.length} filter{filters.length === 1 ? "" : "s"}</Tag>
              </Flex>
            ),
            children: (
              <ServerFilterPanel
                filters={filters}
                editing={editing}
                onChange={(next) => onServerChange(name, { message_filter: next })}
              />
            ),
          };
        })}
      />
    </div>
  );
}

// ── Per-server panel ─────────────────────────────────────────────────────────

function ServerFilterPanel({
  filters,
  editing,
  onChange,
}: {
  filters: McpMessageFilter[];
  editing?: boolean;
  onChange: (next: McpMessageFilter[]) => void;
}) {
  const [tpl, setTpl] = useState<McpFilterTemplateId | undefined>();

  const updateAt = (i: number, next: McpMessageFilter) =>
    onChange(filters.map((f, j) => (j === i ? next : f)));
  const removeAt = (i: number) => onChange(filters.filter((_, j) => j !== i));

  const addTemplate = () => {
    const t = MCP_FILTER_TEMPLATES.find((x) => x.id === tpl);
    if (!t) return;
    onChange([...filters, t.build()]);
    setTpl(undefined);
  };
  const addBlank = () =>
    onChange([
      ...filters,
      { name: "", if_all: [{ property: "/method", value: "tools/call" }], enforce: { property: "/params/name", values: [], allow_list: false } },
    ]);

  if (!editing) {
    return (
      <Flex vertical gap={8}>
        {filters.length === 0 && <Text type="secondary" style={{ fontSize: 12 }}>No custom filters.</Text>}
        {filters.map((f, i) => (
          <FilterCard key={i} filter={f} onChange={() => {}} onRemove={() => {}} readOnly />
        ))}
      </Flex>
    );
  }

  return (
    <Flex vertical gap={10}>
      {filters.length === 0 && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          No filters yet. Add a template below or a blank rule.
        </Text>
      )}
      {filters.map((f, i) => (
        <FilterCard key={i} filter={f} onChange={(next) => updateAt(i, next)} onRemove={() => removeAt(i)} />
      ))}
      <Flex wrap="wrap" align="center" gap={8}>
        <Select
          size="small"
          style={{ minWidth: 200 }}
          placeholder="Add from template…"
          value={tpl}
          onChange={(v) => setTpl(v)}
          options={MCP_FILTER_TEMPLATES.map((t) => ({ value: t.id, label: t.label }))}
        />
        <Button size="small" icon={<Plus size={13} />} disabled={!tpl} onClick={addTemplate}>
          Add
        </Button>
        <Button size="small" type="text" icon={<Plus size={13} />} onClick={addBlank}>
          Blank filter
        </Button>
      </Flex>
    </Flex>
  );
}

// ── One filter card ──────────────────────────────────────────────────────────

function FilterCard({
  filter,
  onChange,
  onRemove,
  readOnly,
}: {
  filter: McpMessageFilter;
  onChange: (next: McpMessageFilter) => void;
  onRemove: () => void;
  readOnly?: boolean;
}) {
  const { token } = theme.useToken();
  return (
    <div style={{ border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 8, padding: "10px 12px" }}>
      <Flex align="center" gap={8} style={{ marginBottom: 8 }}>
        {readOnly ? (
          <code style={{ fontFamily: token.fontFamilyCode, fontSize: 12 }}>{filter.name || "(unnamed)"}</code>
        ) : (
          <Input
            size="small"
            style={{ fontFamily: token.fontFamilyCode, flex: 1 }}
            placeholder="rule name (shown in violation events)"
            value={filter.name}
            onChange={(e) => onChange({ ...filter, name: e.target.value })}
          />
        )}
        {!readOnly && (
          <Button size="small" type="text" danger aria-label="remove filter" icon={<X size={13} />} onClick={onRemove} />
        )}
      </Flex>

      <FieldLabel>When (all conditions match)</FieldLabel>
      <ConditionRows
        conditions={filter.if_all}
        readOnly={readOnly}
        onChange={(next) => onChange({ ...filter, if_all: next })}
      />

      <div style={{ height: 8 }} />
      <FieldLabel>Then enforce</FieldLabel>
      <EnforceRow
        enforce={filter.enforce}
        readOnly={readOnly}
        onChange={(next) => onChange({ ...filter, enforce: next })}
      />
    </div>
  );
}

// ── if_all conditions ────────────────────────────────────────────────────────

function ConditionRows({
  conditions,
  readOnly,
  onChange,
}: {
  conditions: McpIfAllCondition[];
  readOnly?: boolean;
  onChange: (next: McpIfAllCondition[]) => void;
}) {
  const { token } = theme.useToken();
  const update = (i: number, next: McpIfAllCondition) => onChange(conditions.map((c, j) => (j === i ? next : c)));
  const remove = (i: number) => onChange(conditions.filter((_, j) => j !== i));
  const add = () => onChange([...conditions, { property: "/params/name", value: "" }]);

  return (
    <Flex vertical gap={6}>
      {conditions.length === 0 && (
        <Text type="secondary" style={{ fontSize: 12 }}>No conditions — matches every message.</Text>
      )}
      {conditions.map((c, i) => (
        <Flex key={i} align="center" gap={6} wrap="wrap">
          {readOnly ? (
            <code style={{ fontFamily: token.fontFamilyCode, fontSize: 12 }}>{c.property}</code>
          ) : (
            <Input
              size="small"
              style={{ width: 180, fontFamily: token.fontFamilyCode }}
              value={c.property}
              onChange={(e) => update(i, { ...c, property: e.target.value })}
            />
          )}
          <span style={{ color: token.colorTextTertiary }}>=</span>
          {c.property === "/method" ? (
            readOnly ? (
              <Tag style={{ fontFamily: token.fontFamilyCode }}>{c.value || "—"}</Tag>
            ) : (
              <Select
                size="small"
                style={{ minWidth: 160 }}
                placeholder="method…"
                value={c.value || undefined}
                onChange={(v) => update(i, { ...c, value: v })}
                options={MCP_METHODS.map((m) => ({ value: m, label: m }))}
              />
            )
          ) : readOnly ? (
            <code style={{ fontFamily: token.fontFamilyCode, fontSize: 12 }}>{c.value}</code>
          ) : (
            <Input
              size="small"
              style={{ flex: 1, minWidth: 120, fontFamily: token.fontFamilyCode }}
              placeholder="value"
              value={c.value}
              onChange={(e) => update(i, { ...c, value: e.target.value })}
            />
          )}
          {!readOnly && (
            <Button size="small" type="text" aria-label="remove condition" icon={<X size={12} />} onClick={() => remove(i)} />
          )}
        </Flex>
      ))}
      {!readOnly && (
        <Button size="small" type="link" style={{ padding: 0, alignSelf: "flex-start" }} icon={<Plus size={12} />} onClick={add}>
          Add condition
        </Button>
      )}
    </Flex>
  );
}

// ── enforce clause ───────────────────────────────────────────────────────────

function EnforceRow({
  enforce,
  readOnly,
  onChange,
}: {
  enforce: McpEnforceClause;
  readOnly?: boolean;
  onChange: (next: McpEnforceClause) => void;
}) {
  const { token } = theme.useToken();
  return (
    <Flex vertical gap={6}>
      <Flex align="center" gap={8} wrap="wrap">
        {readOnly ? (
          <code style={{ fontFamily: token.fontFamilyCode, fontSize: 12 }}>{enforce.property}</code>
        ) : (
          <Input
            size="small"
            style={{ width: 240, fontFamily: token.fontFamilyCode }}
            placeholder="/params/arguments/cmd"
            value={enforce.property}
            onChange={(e) => onChange({ ...enforce, property: e.target.value })}
          />
        )}
        {readOnly ? (
          <Tag color={enforce.allow_list ? "default" : token.colorError}>{enforce.allow_list ? "Allowlist" : "Denylist"}</Tag>
        ) : (
          <Segmented
            size="small"
            value={enforce.allow_list ? "allow" : "deny"}
            onChange={(v) => onChange({ ...enforce, allow_list: v === "allow" })}
            options={[
              { value: "deny", label: "Denylist" },
              { value: "allow", label: "Allowlist" },
            ]}
          />
        )}
      </Flex>
      <Text type="secondary" style={{ fontSize: 11 }}>
        {enforce.allow_list
          ? "Only listed values are allowed; everything else blocks."
          : "Listed values are blocked; everything else passes."}
      </Text>
      {readOnly ? (
        <Flex wrap="wrap" gap={4}>
          {enforce.values.map((v, i) => (
            <Tag key={i} style={{ fontFamily: token.fontFamilyCode }}>{v}</Tag>
          ))}
          {enforce.values.length === 0 && <Text type="secondary" style={{ fontSize: 12 }}>(none)</Text>}
        </Flex>
      ) : (
        <ChipEditor
          value={enforce.values}
          placeholder={enforce.allow_list ? "allowed value, press Enter" : "blocked value or regex, press Enter"}
          onChange={(vals) => onChange({ ...enforce, values: vals })}
        />
      )}
    </Flex>
  );
}

// FieldLabel — mirrors the small uppercase field labels used across Policy.tsx.
// TODO(wire): if Policy.tsx exports its own FieldLabel, import that instead of
// re-declaring, to keep one styling source.
function FieldLabel({ children }: { children: ReactNode }) {
  const { token } = theme.useToken();
  return (
    <Text
      style={{ display: "block", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, color: token.colorTextSecondary, marginBottom: 4 }}
    >
      {children}
    </Text>
  );
}
